import { Node, mergeAttributes } from '@tiptap/core';

export const FillInBlank = Node.create({
  name: 'fillInBlank',

  group: 'block',

  content: 'inline*',

  atom: true,

  addAttributes() {
    return {
      question: {
        default: '',
      },
      answer: {
        default: '',
      },
      hint: {
        default: '',
      },
      // A blank that is one item of a bulleted list (workbooks that mix
      // printed items and blanks in one list, e.g. BetterMan CORE). Drawn as a
      // compact bullet instead of a separated row (13 Oct 2026).
      listItem: {
        default: false,
        parseHTML: (el) => el.getAttribute('data-list-item') === 'true',
        renderHTML: (attrs) => (attrs.listItem ? { 'data-list-item': 'true' } : {}),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="fill-in-blank"]',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    // Generate a STABLE ID based on question content (so it's the same on every render)
    const question = HTMLAttributes.question || '';
    const answerCSV = HTMLAttributes.answer || '';
    const hint = HTMLAttributes.hint || '';

    // Create a simple hash from the question text for stable ID
    const hashCode = (str) => {
      let hash = 0;
      for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash = hash & hash; // Convert to 32bit integer
      }
      return Math.abs(hash).toString(36);
    };
    const blockId = `fib-${hashCode(question)}`;

    // Parse answers - split by comma, trim whitespace
    const answers = answerCSV.split(',').map((a) => a.trim());

    // Count blanks in question (matches 2+ underscores)
    const blankMatches = question.match(/_{2,}/g) || [];
    const numBlanks = blankMatches.length;

    // Build the question with inline inputs if multiple blanks
    let questionHTML = question;
    let blankIndex = 0;

    if (numBlanks > 0) {
      // Replace each ___ with an inline input placeholder
      // We use data attributes to store the answer for each blank
      questionHTML = question.replace(/_{2,}/g, () => {
        const answer = answers[blankIndex] || '';
        const inputId = `${blockId}-blank-${blankIndex}`;
        blankIndex++;
        // Return a marker that will be processed client-side
        return `<span class="fib-inline-wrapper" data-blank-index="${blankIndex - 1}" data-answer="${answer.replace(/"/g, '&quot;')}">
          <input type="text"
            class="fib-inline-input"
            id="${inputId}"
            placeholder="..."
            autocomplete="off" autocorrect="off" autocapitalize="sentences" spellcheck="true" data-1p-ignore data-lpignore="true" data-form-type="other"
            data-answer="${answer.replace(/"/g, '&quot;')}"
            data-block-id="${blockId}"
            data-blank-index="${blankIndex - 1}"
          />
        </span>`;
      });
    }

    // Build the HTML structure
    const elements = [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-type': 'fill-in-blank',
        'data-block-id': blockId,
        'data-answers': answerCSV,
        'data-num-blanks': numBlanks,
        class: numBlanks > 0 ? 'fill-in-blank-block fill-in-blank-inline' : 'fill-in-blank-block',
      }),
    ];

    // Header with question and action buttons
    const headerContent = ['div', { class: 'fill-in-blank-header' }];

    // Question with inline inputs or plain question
    if (numBlanks > 0) {
      // For multiple blanks, we render the question as HTML with inputs
      headerContent.push([
        'div',
        {
          class: 'question question-inline',
          'data-question-html': questionHTML,
        },
        // The actual HTML will be set via JavaScript after render
        question, // Fallback text
      ]);
    } else {
      // Single blank or no blanks - show question as text
      headerContent.push(['div', { class: 'question' }, question]);
    }

    // Action buttons
    const actionsContent = ['div', { class: 'fill-in-blank-actions' }];

    // Hint button (if hint exists)
    if (hint) {
      actionsContent.push([
        'button',
        {
          type: 'button',
          class: 'hint-button',
          'data-block-id': blockId,
          title: 'Show hint',
        },
        '',
      ]);
    }

    // Answer button
    actionsContent.push([
      'button',
      {
        type: 'button',
        class: 'answer-button',
        'data-block-id': blockId,
        'data-answers': answerCSV,
        title: 'Fill in answers',
      },
      '',
    ]);

    headerContent.push(actionsContent);
    elements.push(headerContent);

    // Only show single input if no inline blanks
    if (numBlanks === 0) {
      elements.push([
        'input',
        {
          type: 'text',
          class: 'blank-input',
          placeholder: 'Type your answer...',
          'data-answer': answerCSV,
          'data-block-id': blockId,
        },
      ]);
    }

    // Hint box (if hint exists)
    if (hint) {
      elements.push([
        'div',
        {
          class: 'hint-box',
          'data-block-id': blockId,
          style: 'display: none;',
        },
        `${hint}`,
      ]);
    }

    // Answer box
    elements.push([
      'div',
      {
        class: 'answer-box',
        'data-block-id': blockId,
        style: 'display: none;',
      },
      `${answerCSV}`,
    ]);

    return elements;
  },

  addCommands() {
    return {
      setFillInBlank:
        (attributes) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: attributes,
          });
        },
    };
  },
});

export default FillInBlank;
